import {
  useLocation,
} from "react-router-dom";
import { TRANSACTION_REPORT, TRANSACTION_REPORT_COUNT } from "./fragment";
import { ReportFilters } from "../../components/ReportFilters";
import ReportHeader from "../../components/ReportHeader";
import { userStore } from "../../../../store/user";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { organizationStore } from "../../../../store/organization";
import { TransactionReportRow } from "./type";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import { eventStore, useEventStore } from "../../../../store/event";
import { useReportFiltersStore } from "../../../../store/reportFilters";
import { useOrganizationStore } from "../../../../store/organization";
import { useAccumulatedOptions, useLocationFilterOptions } from "../../components/useFilterOptions";
import { CsvCell, GridApi, SizeColumnsToContentStrategy } from "ag-grid-community";
import { buildReportCsvHeader } from "../../utils/buildReportCsvHeader";
import { SecondaryButton } from "../../../../components/Button";
import { AgGridReact } from "ag-grid-react";
import { colDefs } from "./helpers";
import Loader from "../../../../components/Loader";
import { fetchReceiptPhonesByReference } from "../../utils/api";

const client = createApolloClient();
const { getState } = userStore;
const { getState: getOrganizationState } = organizationStore;
const { getState: getEventState } = eventStore;
// 10k rows/page: each response is parsed as one giant JSON string, so very
// large pages (50k rows incl. items_json) caused transient allocation spikes
// that pushed big-event exports over the renderer memory limit.
const PAGE_SIZE = 10000;
const DEBOUNCE_DELAY = 100;

export const TransactionsReport = () => {
  const [data, setData] = useState<TransactionReportRow[]>([]);
  const [receiptPhones, setReceiptPhones] = useState<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  // Set when the last load failed, so the page never keeps showing the previous
  // org's / event's rows as if they were this one's.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedProcessors, setSelectedProcessors] = useState<string[] | null>(null);

  const gridRef = useRef<GridApi>();
  const rowHeight = 50;
  const autoSizeStrategy: SizeColumnsToContentStrategy = { type: "fitCellContents" };
  const location = useLocation();
  const fetchTimeoutRef = useRef<any>(null);

  // Location & event data for processor enrichment
  const allLocations = useReportFiltersStore((state) => state.allLocations);
  const allEvents = useEventStore((state) => state.allEvents);
  const currentEventId = getEventState().eventId;
  const filterEventId = useEventStore((state) => state.eventId);
  const filterOrganizationId = useOrganizationStore((state) => state.organizationId);

  // Enrich rows with payment processor name, config id, and expected deposit
  const enrichedData = useMemo(() => {
    const locationProcessorMap = new Map<number, { name: string; configId: number | null; processingRate: number; feePerTransaction: number }>();
    for (const loc of allLocations as any[]) {
      if (loc.payment_processor_config?.payment_processor) {
        locationProcessorMap.set(Number(loc.id), {
          name: loc.payment_processor_config.payment_processor,
          configId: loc.payment_processor_config.id ?? null,
          processingRate: loc.payment_processor_config.processing_rate ?? 0,
          feePerTransaction: loc.payment_processor_config.config?.application_fee_per_transaction ?? 0,
        });
      }
    }

    const currentEvent = allEvents?.find((e: any) => e.id === currentEventId);
    const eventProcessorName = currentEvent?.payment_processor_config?.payment_processor ?? '';
    const eventProcessorConfigId = currentEvent?.payment_processor_config?.id ?? null;
    const eventProcessingRate = currentEvent?.payment_processor_config?.processing_rate ?? 0;
    const eventFeePerTransaction = currentEvent?.payment_processor_config?.config?.application_fee_per_transaction ?? 0;

    return data.map((row) => {
      const locProcessor = locationProcessorMap.get(row.location_id);
      const processingRate = locProcessor?.processingRate ?? eventProcessingRate;
      const feePerTransaction = locProcessor?.feePerTransaction ?? eventFeePerTransaction;
      const paymentType = String(row.payment_type).toLowerCase();
      const isProcessorPayment = paymentType === 'credit' || paymentType === 'rfid';
      const expectedDeposit = isProcessorPayment
        ? Math.round(row.transaction_amount * (1 - processingRate))
        : undefined;

      const referenceId = row.order_payment?.reference_id;
      const receiptPhone = referenceId ? receiptPhones.get(String(referenceId)) : undefined;

      return {
        ...row,
        receipt_phone_number: receiptPhone,
        payment_processor_name: locProcessor?.name || eventProcessorName || undefined,
        payment_processor_config_id: locProcessor?.configId ?? eventProcessorConfigId ?? undefined,
        processing_rate: processingRate || undefined,
        fee_per_transaction: feePerTransaction || undefined,
        expected_deposit: expectedDeposit,
      };
    });
  }, [data, receiptPhones, allLocations, allEvents, currentEventId]);

  // Derive payment processor options from enriched data
  const paymentProcessorOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of enrichedData) {
      if (r.payment_processor_name) set.add(r.payment_processor_name);
    }
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      .map((name) => ({ label: name, value: name }));
  }, [enrichedData]);

  // Apply processor filter
  const filteredData = useMemo(() => {
    if (!selectedProcessors || selectedProcessors.length === 0) return enrichedData;
    return enrichedData.filter(
      (row) => row.payment_processor_name && selectedProcessors.includes(row.payment_processor_name)
    );
  }, [enrichedData, selectedProcessors]);

  const handlePaymentProcessorChange = useCallback((value: string[] | null) => {
    setSelectedProcessors(value);
  }, []);

  // === Filter options ===
  // Usernames: every value seen for this event, not only the current rows,
  // so a second pick is possible while the first filters the rows.
  const usernameOptions = useAccumulatedOptions(
      filteredData,
      (r) => r.username,
      `${filterOrganizationId ?? ""}:${filterEventId ?? ""}`
  );

  // Locations: the event's active locations (the Vendor dropdown's source),
  // not the rows, which are already filtered by location.
  const locationOptions = useLocationFilterOptions();

  const handleExportCSV = () => {
    if (gridRef.current) {
      const timestamp = new Date().toISOString().replace(/[-:]/g, "");
      const params = {
        fileName: `TransactionsReport_${timestamp}.csv`,
        columnSeparator: ",",
        skipHeader: false,
        allColumns: true,
        prependContent: buildReportCsvHeader({
          reportName: "Transactions Report",
          searchString: location.search,
        }),
      };
      gridRef.current.exportDataAsCsv(params);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchTransactions = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const rawOrganizationId = getOrganizationState().organizationId;
        const organizationId = parseInt(
            rawOrganizationId !== null ? rawOrganizationId.toString() : "",
            10
        );
        const { eventId } = getEventState();
        const user = getState().user;

        const search = new URLSearchParams(location.search);
        const where = search.get("where");
        // RP-1937: no filter in the address yet; ReportFilters re-applies it and this runs again.
        if (!where) return;
        let whereVariable: any = where ? JSON.parse(where) : {};

        if (organizationId && !Number.isNaN(organizationId) && organizationId !== 0) {
          whereVariable = {
            ...whereVariable,
            organization_id: { _eq: organizationId },
          };
        }

        if (user?.vendors && !whereVariable.vendor_id) {
          whereVariable = {
            ...whereVariable,
            vendor_id: { _in: user.vendors },
          };
        }

        if (eventId) {
          whereVariable = {
            ...whereVariable,
            event_id: { _eq: eventId },
          };
        } else if (user?.events?.length) {
          whereVariable = {
            ...whereVariable,
            event_id: { _in: user.events },
          };
        }

        whereVariable = {
          ...whereVariable,
          payment_status: { _nin: ["pending", "pending1"] },
        };

        const { data: totalData } = await client.query({
          query: TRANSACTION_REPORT_COUNT,
          variables: { where: whereVariable, limit: 0 },
          // no-cache: report rows must not be written into the shared Apollo
          // InMemoryCache — on large events that duplicates the whole dataset
          // in memory (keyed per offset, never evicted) and OOMs the tab.
          fetchPolicy: "no-cache",
        });

        const totalCount = totalData.total.aggregate.count as number;
        let offset = 0;
        const chunkData: TransactionReportRow[] = [];

        while (offset < totalCount) {
          const { data: chunk } = await client.query({
            query: TRANSACTION_REPORT,
            variables: {
              where: whereVariable,
              limit: PAGE_SIZE,
              offset,
            },
            fetchPolicy: "no-cache",
          });
          // push in a plain loop: `[...acc, ...chunk]` re-copies the whole
          // accumulator every page (O(n²) allocation churn on big events), and
          // `push(...chunk)` can overflow the arg stack at this page size.
          for (const row of chunk.reports_transactions || []) {
            chunkData.push(row);
          }
          offset += PAGE_SIZE;
        }

        // Backfill phone numbers from receipt_track (the number the customer
        // typed for their receipt) keyed by order_payment.reference_id.
        const phoneMap = await fetchReceiptPhonesByReference(
          client,
          chunkData.map((row) => row.order_payment?.reference_id),
          organizationId
        );

        if (isMounted) {
          setReceiptPhones(phoneMap);
          setData(chunkData);
        }
      } catch (error) {
        console.error("Error fetching transactions:", error);
        // Clear the previous results: leaving them up made another org's rows
        // look like this org's (a big org timing out showed Ronin's test sales).
        if (isMounted) {
          setData([]);
          setReceiptPhones(new Map());
          setLoadError(
            "The report didn't finish loading, so nothing is shown. This usually means too much data was asked for at once. Pick an event or a shorter date range and try again."
          );
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    const debouncedFetch = () => {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = setTimeout(fetchTransactions, DEBOUNCE_DELAY);
    };

    debouncedFetch();

    return () => {
      isMounted = false;
      clearTimeout(fetchTimeoutRef.current);
    };
  }, [location.search]);

  const gridOptions = { enableCellTextSelection: true };

  return (
      <>
        <div className="bg-white px-4 pb-1 mb-3 rounded-md">
          <ReportHeader title="Transactions Report" />
          <ReportFilters
          includeSubEvents
              includeDates
              includeTime
              includeLocations
              includeVendors
              eventScopedVendors
              includeUsernames
              includePaymentProcessors
              usernameOptions={usernameOptions}
              locationOptions={locationOptions}
              paymentProcessorOptions={paymentProcessorOptions}
              selectedPaymentProcessors={selectedProcessors}
              onPaymentProcessorChange={handlePaymentProcessorChange}
              customQueryPaths={{
                dateFrom: "_and[0].transaction_hour",
                dateTo: "_and[1].transaction_hour",
              }}
          />
          <div className="flex">
            <div className="ml-auto">
              <SecondaryButton onClick={handleExportCSV}>Export to CSV</SecondaryButton>
            </div>
          </div>
        </div>

        {loadError && !isLoading && (
            <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
              {loadError}
            </div>
        )}

        <div style={{ height: "60vh" }}>
          <div className={"ag-theme-quartz"} style={{ width: "100%", height: "100%" }}>
            {isLoading ? (
                <Loader />
            ) : (
                <AgGridReact
                    rowData={filteredData}
                    columnDefs={colDefs}
                    rowHeight={rowHeight}
                    autoSizeStrategy={autoSizeStrategy}
                    pagination={true}
                    gridOptions={gridOptions}
                    paginationPageSize={100}
                    onGridReady={(params) => (gridRef.current = params.api)}
                />
            )}
          </div>
        </div>
      </>
  );
};
